import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, Play, RotateCcw, Trophy, Target, Clock,
  AlertTriangle, CheckCircle, XCircle, Volume2, VolumeX,
  ChevronRight, Star, ArrowUp, ArrowDown, Gauge, Radio, Bell, Eye, Brain,
  MessageSquare, Hand, Lightbulb, AlertCircle, Zap, Monitor, Smartphone,
  Square, Circle, Crown, Database
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { apiClient, useAuth } from '../lib/api';
import { toast } from 'sonner';
import { MaintenanceNotice, MaintenanceBanner } from '../components/MaintenanceNotice';
import { getEntitlements } from '../lib/entitlements';

// Import enhanced game components
import SelectiveAttentionGame from '../components/games/SelectiveAttentionGame';
import DividedAttentionGame from '../components/games/DividedAttentionGame';
import VigilanceGame from '../components/games/VigilanceGame';
import PerceptionGame from '../components/games/PerceptionGame';
import DialsGame from '../components/games/DialsGame';
import MultitaskingGame from '../components/games/MultitaskingGame';
import RulesMemoryGame from '../components/games/RulesMemoryGame';
import CommunicationGame from '../components/games/CommunicationGame';
import BehaviouralGame from '../components/games/BehaviouralGame';
import SequencingGame from '../components/games/SequencingGame';
import RulesAcquisitionGame from '../components/games/RulesAcquisitionGame';
import DetailCheckingGame from '../components/games/DetailCheckingGame';
import NorthernScreeningAssessment from '../components/games/NorthernScreeningAssessment';
import VisualSearchGame from '../components/games/VisualSearchGame';

// Mapping of test slugs to the real railway assessments they prepare for
const realTestMapping = {
  'attention-selective': 'Group Bourdon / TEA-Occ Selective Attention Test',
  'attention-divided': 'TEA-Occ Divided Attention Test',
  'vigilance': 'WAFV Vigilance Test',
  'memory': 'TRP1 Trainability for Rules and Procedures Test (Part 1 - Recall)',
  'sequencing': 'TRP2 Trainability for Rules and Procedures Test (Part 2 - Sequencing)',
  'rules-acquisition': 'Rules Acquisition Aptitude Test (RAAT)',
  'reasoning': 'TRP2 Trainability Problem-Solving Test',
  'rule-based-prioritising': 'TRP1/TRP2 Rule Application Tests',
  'perception': 'ATAVT Adaptive Tachistoscopic Traffic Perception Test',
  'reaction-time': 'WAFV/ATAVT Reaction Time Tests',
  'hand-coordination': '2HAND Two-Hand Coordination Test',
  'communication-accuracy': 'MMI Communication Assessment',
  'behavioural': 'Situational Judgement Exercise (SJE)',
  'multitasking-workload': 'TEA-Occ Workload Management Tests',
  'detail-checking': 'Detail Checking & Data Comparison Test',
  'northern-online-screening': 'Northern style Maki test practice assessment',
  'visual-search': 'OPC Visual Search Exercise (VSE)',
};

// Game State Types
const GAME_STATES = {
  READY: 'ready',
  DEVICE_SELECT: 'device_select',
  PLAYING: 'playing',
  PAUSED: 'paused',
  FINISHED: 'finished',
};

// Device Mode Selection Component
const DeviceModeSelector = ({ onSelect, testName }) => (
  <Card className="border-slate-200">
    <CardContent className="pt-8">
      <div className="text-center mb-6">
        <h2 className="font-heading text-xl font-bold text-[#0B1D51] mb-2">
          Select Your Device
        </h2>
        <p className="text-sm text-[#64748B]">
          Choose how you're taking this test for the best experience
        </p>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <Button
          variant="outline"
          className="h-32 flex-col gap-3 hover:border-[#0B1D51] hover:bg-[#0B1D51]/5"
          onClick={() => onSelect('desktop')}
          data-testid="select-desktop"
        >
          <Monitor className="w-10 h-10 text-[#0B1D51]" />
          <div>
            <p className="font-semibold">Laptop/Desktop</p>
            <p className="text-xs text-[#64748B]">Keyboard controls</p>
          </div>
        </Button>
        
        <Button
          variant="outline"
          className="h-32 flex-col gap-3 hover:border-[#FF9F1C] hover:bg-[#FF9F1C]/5"
          onClick={() => onSelect('mobile')}
          data-testid="select-mobile"
        >
          <Smartphone className="w-10 h-10 text-[#FF9F1C]" />
          <div>
            <p className="font-semibold">Mobile/Tablet</p>
            <p className="text-xs text-[#64748B]">Touch controls</p>
          </div>
        </Button>
      </div>
    </CardContent>
  </Card>
);

// Base Game Component
const GameWrapper = ({ children, test, level, onBack, slug }) => {
  const realTestName = realTestMapping[slug];
  const showLevel = slug !== 'northern-online-screening';
  
  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="glass-header sticky top-0 z-50 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16">
            <button 
              onClick={onBack}
              className="flex items-center gap-2 text-[#64748B] hover:text-[#0B1D51]"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Back</span>
            </button>
            <div className="text-center">
              <h1 className="font-heading text-base sm:text-lg font-bold text-[#0B1D51]">
                {test?.name || 'Loading...'}
              </h1>
              {realTestName && (
                <p className="text-xs text-[#FF9F1C] font-medium">
                  Develops skills for: {realTestName}
                </p>
              )}
              {showLevel && <p className="text-xs text-[#64748B]">Level {level}</p>}
            </div>
            <div className="w-16 sm:w-20" />
          </div>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-3 sm:px-4 py-4 sm:py-8">
        {children}
      </main>
    </div>
  );
};

// Timer Component
const GameTimer = ({ timeLeft, maxTime, showMinutes = false }) => {
  const percentage = (timeLeft / maxTime) * 100;
  const isWarning = percentage < 25;
  
  const formatTime = (seconds) => {
    if (showMinutes) {
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    }
    return `${Math.ceil(seconds)}s`;
  };
  
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span className="text-[#64748B]">Time Remaining</span>
        <span className={`font-mono font-medium ${isWarning ? 'text-red-500' : 'text-[#0B1D51]'}`}>
          {formatTime(timeLeft)}
        </span>
      </div>
      <div className="h-1 bg-slate-200 rounded-full overflow-hidden">
        <div 
          className={`h-full transition-all duration-100 ${isWarning ? 'bg-red-500' : 'bg-[#FF9F1C]'}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

// Score Display Component
const ScoreDisplay = ({ score, accuracy, label = 'Score' }) => (
  <div className="flex items-center gap-4">
    <div className="text-center">
      <p className="text-xs text-[#64748B] uppercase tracking-wide">{label}</p>
      <p className="font-mono text-xl sm:text-2xl font-bold text-[#0B1D51]">{score}</p>
    </div>
    {accuracy !== undefined && (
      <div className="text-center">
        <p className="text-xs text-[#64748B] uppercase tracking-wide">Accuracy</p>
        <p className="font-mono text-xl sm:text-2xl font-bold text-[#2CB67D]">{accuracy.toFixed(0)}%</p>
      </div>
    )}
  </div>
);

// Results Screen Component
const ResultsScreen = ({ score, maxScore, accuracy, timeSpent, onReplay, onNext, onBack, testSlug, level, isMember, isGuestTrial }) => {
  const percentage = (score / maxScore) * 100;
  const stars = percentage >= 90 ? 3 : percentage >= 70 ? 2 : percentage >= 50 ? 1 : 0;
  
  // Performance analysis based on score
  const getPerformanceAnalysis = () => {
    if (percentage >= 90) {
      return {
        title: "Outstanding Performance!",
        message: "You've demonstrated excellent skills in this assessment. Your performance indicates strong aptitude in this area. Keep up the great work and continue to challenge yourself with higher levels.",
        icon: "success",
        tip: "Consider moving to the next level to further develop your skills."
      };
    } else if (percentage >= 75) {
      return {
        title: "Great Performance!",
        message: "You've shown good competency in this assessment. Your results are above average and indicate solid foundational skills. With continued practice, you can achieve even better results.",
        icon: "good",
        tip: "Focus on areas where you hesitated to improve your confidence."
      };
    } else if (percentage >= 60) {
      return {
        title: "Good Progress!",
        message: "You're developing the skills tested in this assessment. Your performance shows understanding of the core concepts. Regular practice will help you build speed and accuracy.",
        icon: "progress",
        tip: "Try replaying this level to reinforce your learning before advancing."
      };
    } else if (percentage >= 40) {
      return {
        title: "Building Foundations",
        message: "This assessment area requires more practice. Don't be discouraged – these skills develop with repetition. Focus on understanding the patterns and take your time with each question.",
        icon: "developing",
        tip: "Review the test instructions and practice at a comfortable pace."
      };
    } else {
      return {
        title: "Keep Practising!",
        message: "Every expert was once a beginner. This assessment type may be new to you, and that's okay. Regular practice sessions will help you understand what's expected and improve your performance significantly.",
        icon: "encourage",
        tip: "Start slowly, focus on accuracy over speed, and you'll see improvement."
      };
    }
  };

  const analysis = getPerformanceAnalysis();
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="text-center"
    >
      <Card className="border-slate-200 max-w-md mx-auto">
        <CardContent className="pt-8 pb-6">
          <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 sm:mb-6 bg-[#0B1D51]/5 rounded-full flex items-center justify-center">
            <Trophy className={`w-8 h-8 sm:w-10 sm:h-10 ${percentage >= 70 ? 'text-[#FF9F1C]' : 'text-[#64748B]'}`} />
          </div>
          
          <h2 className="font-heading text-xl sm:text-2xl font-bold text-[#0B1D51] mb-2">
            {analysis.title}
          </h2>
          
          <div className="flex justify-center gap-1 mb-4 sm:mb-6">
            {[1, 2, 3].map((i) => (
              <Star 
                key={i} 
                className={`w-6 h-6 sm:w-8 sm:h-8 ${i <= stars ? 'text-[#FF9F1C] fill-[#FF9F1C]' : 'text-slate-200'}`}
              />
            ))}
          </div>
          
          <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-4 sm:mb-6 py-4 border-y">
            <div>
              <p className="text-xs text-[#64748B] uppercase">Score</p>
              <p className="font-mono text-lg sm:text-xl font-bold text-[#0B1D51]">{score}/{maxScore}</p>
            </div>
            <div>
              <p className="text-xs text-[#64748B] uppercase">Accuracy</p>
              <p className="font-mono text-lg sm:text-xl font-bold text-[#2CB67D]">{accuracy.toFixed(0)}%</p>
            </div>
            <div>
              <p className="text-xs text-[#64748B] uppercase">Time</p>
              <p className="font-mono text-lg sm:text-xl font-bold text-[#0B1D51]">{timeSpent}s</p>
            </div>
          </div>

          {/* Performance Analysis */}
          <div className={`p-4 rounded-lg mb-4 sm:mb-6 text-left ${
            percentage >= 75 ? 'bg-[#2CB67D]/10 border border-[#2CB67D]/20' : 
            percentage >= 50 ? 'bg-[#FF9F1C]/10 border border-[#FF9F1C]/20' : 
            'bg-[#0B1D51]/5 border border-[#0B1D51]/10'
          }`}>
            <p className="text-sm text-[#64748B] mb-2">
              {analysis.message}
            </p>
            <p className="text-xs text-[#0B1D51] font-medium flex items-start gap-2">
              <Lightbulb className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{analysis.tip}</span>
            </p>
          </div>
          
          <div className="space-y-3">
            {/* Guest Trial CTA - Show signup prompt prominently */}
            {isGuestTrial && (
              <div className="bg-[#2CB67D]/10 border border-[#2CB67D]/30 rounded-lg p-4 mb-4">
                <p className="text-sm text-[#2CB67D] font-medium mb-3">
                  Your score wasn't saved. Create a free account to:
                </p>
                <ul className="text-xs text-[#64748B] text-left space-y-1 mb-3">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-3 h-3 text-[#2CB67D]" />
                    Save scores & track progress
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-3 h-3 text-[#2CB67D]" />
                    Get 5 free test attempts
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-3 h-3 text-[#2CB67D]" />
                    See how you compare to others
                  </li>
                </ul>
                <Link to="/?auth=register">
                  <Button className="w-full bg-[#2CB67D] hover:bg-[#2CB67D]/90" data-testid="signup-after-trial-btn">
                    Create Free Account
                  </Button>
                </Link>
              </div>
            )}

            {!isGuestTrial && (
              <Button 
                onClick={onReplay}
                variant="outline"
                className="w-full"
                data-testid="replay-btn"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Play Again
              </Button>
            )}
            
            {!isGuestTrial && level < 5 && (
              isMember || level < 1 ? (
                <Button 
                  onClick={onNext}
                  className="w-full bg-[#FF9F1C] hover:bg-[#FF9F1C]/90"
                  data-testid="next-level-btn"
                >
                  Next Level
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Link to="/pricing" className="block">
                  <Button className="w-full bg-[#0B1D51] hover:bg-[#0B1D51]/90">
                    Upgrade to Unlock Level {level + 1}
                  </Button>
                </Link>
              )
            )}
            
            <Button 
              onClick={onBack}
              variant="ghost"
              className="w-full text-[#64748B]"
              data-testid="back-to-test-btn"
            >
              {isGuestTrial ? 'Try Another Test' : 'Back to Test Details'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

// Old game components removed - using imported components from ../components/games/

const HandCoordinationGame = ({ level, onComplete }) => {
  const [gameState, setGameState] = useState(GAME_STATES.READY);
  const [deviceMode, setDeviceMode] = useState(null);
  const [round, setRound] = useState(0);
  const [targetPosition, setTargetPosition] = useState({ x: 50, y: 50 });
  const [cursorPosition, setCursorPosition] = useState({ x: 50, y: 50 });
  const [isTracking, setIsTracking] = useState(false);
  const [roundComplete, setRoundComplete] = useState(false);
  const [roundAccuracies, setRoundAccuracies] = useState([]);
  const totalRounds = 5;
  const roundDuration = 12000; // 12 seconds per round
  const startTime = useRef(Date.now());
  const gameAreaRef = useRef(null);
  const animationRef = useRef(null);
  const trackingDataRef = useRef([]);
  const roundTimerRef = useRef(null);
  const lastFrameTimeRef = useRef(0); // For delta-time calculation

  // More varied movement with acceleration and direction changes
  const targetRef = useRef({ 
    x: 50, y: 50, 
    dx: 0, dy: 0, 
    ax: 0, ay: 0,
    changeTimer: 0
  });

  // Level-based speed: Level 1 slow, Level 5 fast
  // These are now pixels-per-second based for consistent movement across devices
  // Level 1: 40px/s, Level 2: 60px/s, Level 3: 80px/s, Level 4: 100px/s, Level 5: 120px/s
  const baseSpeedPerSecond = 20 + (level * 20);
  
  // Detect if mobile for slight speed adjustment
  const isMobile = deviceMode === 'mobile';
  // Mobile gets 15% slower to account for touch input latency
  const speedMultiplier = isMobile ? 0.85 : 1.0;

  const randomizeDirection = () => {
    const angle = Math.random() * Math.PI * 2;
    const speedVariation = 0.7 + Math.random() * 0.6;
    const speed = baseSpeedPerSecond * speedVariation * speedMultiplier;
    targetRef.current.dx = Math.cos(angle) * speed;
    targetRef.current.dy = Math.sin(angle) * speed;
    targetRef.current.ax = (Math.random() - 0.5) * 3; // Acceleration in px/s²
    targetRef.current.ay = (Math.random() - 0.5) * 3;
    targetRef.current.changeTimer = 1 + Math.random() * 2; // Change every 1-3 seconds
  };

  const startRound = useCallback(() => {
    setIsTracking(true);
    setRoundComplete(false);
    trackingDataRef.current = [];
    lastFrameTimeRef.current = performance.now();
    
    // Start from random position
    const startX = 20 + Math.random() * 60;
    const startY = 20 + Math.random() * 60;
    targetRef.current = { 
      x: startX, y: startY, 
      dx: 0, dy: 0, 
      ax: 0, ay: 0,
      changeTimer: 0
    };
    randomizeDirection();
    setTargetPosition({ x: startX, y: startY });
    setCursorPosition({ x: 50, y: 50 });
    
    const moveTarget = (currentTime) => {
      const t = targetRef.current;
      
      // Calculate delta time in seconds for frame-rate independent movement
      const deltaTime = Math.min((currentTime - lastFrameTimeRef.current) / 1000, 0.1); // Cap at 100ms to prevent huge jumps
      lastFrameTimeRef.current = currentTime;
      
      // Apply acceleration (scaled by delta time)
      t.dx += t.ax * deltaTime;
      t.dy += t.ay * deltaTime;
      
      // Clamp speed
      const currentSpeed = Math.sqrt(t.dx * t.dx + t.dy * t.dy);
      const maxSpeed = baseSpeedPerSecond * 1.5 * speedMultiplier;
      if (currentSpeed > maxSpeed) {
        t.dx = (t.dx / currentSpeed) * maxSpeed;
        t.dy = (t.dy / currentSpeed) * maxSpeed;
      }
      
      // Move position (scaled by delta time - this is the key fix!)
      t.x += t.dx * deltaTime;
      t.y += t.dy * deltaTime;
      
      // Bounce off walls with some randomization
      if (t.x <= 8 || t.x >= 92) {
        t.dx = -t.dx * (0.8 + Math.random() * 0.4);
        t.x = t.x <= 8 ? 8 : 92;
        randomizeDirection();
      }
      if (t.y <= 8 || t.y >= 92) {
        t.dy = -t.dy * (0.8 + Math.random() * 0.4);
        t.y = t.y <= 8 ? 8 : 92;
        randomizeDirection();
      }
      
      // Random direction changes (time-based, not frame-based)
      t.changeTimer -= deltaTime;
      if (t.changeTimer <= 0) {
        randomizeDirection();
      }
      
      setTargetPosition({ x: t.x, y: t.y });
      animationRef.current = requestAnimationFrame(moveTarget);
    };
    
    animationRef.current = requestAnimationFrame(moveTarget);
    
    // End round after duration
    roundTimerRef.current = setTimeout(() => {
      cancelAnimationFrame(animationRef.current);
      setIsTracking(false);
      setRoundComplete(true);
      
      const avgAccuracy = trackingDataRef.current.length > 0 
        ? Math.round(trackingDataRef.current.reduce((a, b) => a + b, 0) / trackingDataRef.current.length)
        : 0;
      
      setRoundAccuracies(prev => {
        const newAccuracies = [...prev, avgAccuracy];
        
        // Check if game is complete
        if (newAccuracies.length >= totalRounds) {
          const finalScore = Math.round(newAccuracies.reduce((a, b) => a + b, 0) / newAccuracies.length);
          setTimeout(() => {
            setGameState(GAME_STATES.FINISHED);
            onComplete({
              score: finalScore,
              maxScore: 100,
              accuracy: finalScore,
              completionTime: Math.floor((Date.now() - startTime.current) / 1000)
            });
          }, 1000);
        }
        return newAccuracies;
      });
    }, roundDuration);
  }, [baseSpeedPerSecond, speedMultiplier, onComplete]);

  // Start next round
  useEffect(() => {
    if (gameState === GAME_STATES.PLAYING && roundComplete && roundAccuracies.length < totalRounds) {
      const timer = setTimeout(() => {
        setRound(r => r + 1);
        startRound();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [gameState, roundComplete, roundAccuracies.length, startRound]);

  // Initial start
  useEffect(() => {
    if (gameState === GAME_STATES.PLAYING && round === 0 && !isTracking && roundAccuracies.length === 0) {
      startRound();
    }
  }, [gameState, round, isTracking, roundAccuracies.length, startRound]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cancelAnimationFrame(animationRef.current);
      clearTimeout(roundTimerRef.current);
    };
  }, []);

  const handleMouseMove = (e) => {
    if (!isTracking || !gameAreaRef.current) return;
    const rect = gameAreaRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setCursorPosition({ x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) });
    
    const distance = Math.sqrt(Math.pow(x - targetPosition.x, 2) + Math.pow(y - targetPosition.y, 2));
    const accuracy = Math.max(0, 100 - distance * 2.5);
    trackingDataRef.current.push(accuracy);
  };

  const handleTouchMove = (e) => {
    if (!isTracking || !gameAreaRef.current) return;
    e.preventDefault();
    const rect = gameAreaRef.current.getBoundingClientRect();
    const touch = e.touches[0];
    const x = ((touch.clientX - rect.left) / rect.width) * 100;
    const y = ((touch.clientY - rect.top) / rect.height) * 100;
    setCursorPosition({ x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) });
    
    const distance = Math.sqrt(Math.pow(x - targetPosition.x, 2) + Math.pow(y - targetPosition.y, 2));
    const accuracy = Math.max(0, 100 - distance * 2.5);
    trackingDataRef.current.push(accuracy);
  };

  if (gameState === GAME_STATES.READY) {
    return (
      <DeviceModeSelector 
        onSelect={(mode) => { 
          setDeviceMode(mode); 
          setGameState(GAME_STATES.DEVICE_SELECT);
        }} 
        testName="Hand-Eye Coordination Test"
      />
    );
  }

  if (gameState === GAME_STATES.DEVICE_SELECT) {
    return (
      <Card className="border-slate-200">
        <CardContent className="pt-6 sm:pt-8">
          <div className="text-center mb-4 sm:mb-6">
            <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 bg-[#2CB67D]/10 rounded-full flex items-center justify-center">
              <Hand className="w-8 h-8 sm:w-10 sm:h-10 text-[#2CB67D]" />
            </div>
            <h2 className="font-heading text-xl sm:text-2xl font-bold text-[#0B1D51] mb-2">
              Motor Coordination
            </h2>
            <p className="text-sm text-[#64748B]">Level {level} - Motor Tracking Assessment</p>
            <p className="text-xs text-[#FF9F1C] font-medium mt-2">
              Develops skills to improve performance on the 2HAND Two-Hand Coordination Test
            </p>
          </div>
          
          <div className="bg-[#F8FAFC] rounded-sm p-3 sm:p-4 mb-4 sm:mb-6">
            <h3 className="font-semibold text-[#0B1D51] mb-3 text-sm sm:text-base">How to Play:</h3>
            <ol className="space-y-2 text-xs sm:text-sm text-[#64748B]">
              <li className="flex gap-2">
                <span className="font-mono text-[#0B1D51]">1.</span>
                A green target will move around the screen
              </li>
              <li className="flex gap-2">
                <span className="font-mono text-[#0B1D51]">2.</span>
                {deviceMode === 'mobile' ? 'Drag your finger' : 'Move your cursor'} to follow the target
              </li>
              <li className="flex gap-2">
                <span className="font-mono text-[#0B1D51]">3.</span>
                Keep your cursor as close to the target as possible
              </li>
              <li className="flex gap-2">
                <span className="font-mono text-[#0B1D51]">4.</span>
                Complete 5 tracking rounds
              </li>
            </ol>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 mb-4 sm:mb-6">
            <p className="text-xs sm:text-sm text-amber-800">
              <strong>RIS-3751-TOM:</strong> This exercise develops skills assessed by the 2HAND test - measuring hand-eye coordination, fine motor control, and ability to track moving objects smoothly.
            </p>
          </div>
          
          <Button 
            onClick={() => {
              setGameState(GAME_STATES.PLAYING);
              startTime.current = Date.now();
            }}
            size="lg"
            className="w-full bg-[#0B1D51] hover:bg-[#0B1D51]/90"
            data-testid="start-game-btn"
          >
            <Play className="w-5 h-5 mr-2" />
            Start Test
          </Button>
        </CardContent>
      </Card>
    );
  }

  const currentAvg = roundAccuracies.length > 0 
    ? Math.round(roundAccuracies.reduce((a, b) => a + b, 0) / roundAccuracies.length) 
    : 0;

  return (
    <div className="min-h-[500px] flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-xl">
        <div className="flex justify-between items-center mb-4 text-sm">
          <span className="font-mono text-[#64748B]">Round {round + 1}/{totalRounds}</span>
          <span className="font-mono text-[#0B1D51]">
            Avg Accuracy: {currentAvg}%
          </span>
        </div>
        
        <div 
          ref={gameAreaRef}
          className="relative w-full h-[400px] bg-slate-900 rounded-lg overflow-hidden cursor-none touch-none"
          onMouseMove={handleMouseMove}
          onTouchMove={handleTouchMove}
        >
          {/* Target */}
          <motion.div
            className="absolute w-12 h-12 rounded-full bg-[#2CB67D] border-4 border-[#2CB67D]/50"
            style={{
              left: `${targetPosition.x}%`,
              top: `${targetPosition.y}%`,
              transform: 'translate(-50%, -50%)'
            }}
            animate={{ scale: isTracking ? 1 : 1.2 }}
          />
          
          {/* Cursor */}
          <div
            className="absolute w-4 h-4 rounded-full bg-[#FF9F1C] border-2 border-white pointer-events-none"
            style={{
              left: `${cursorPosition.x}%`,
              top: `${cursorPosition.y}%`,
              transform: 'translate(-50%, -50%)'
            }}
          />
          
          {/* Round complete overlay */}
          {roundComplete && roundAccuracies.length < totalRounds && (
            <div className="absolute inset-0 flex items-center justify-center bg-slate-900/80">
              <div className="text-center">
                <p className="text-white text-2xl font-bold mb-2">Round Complete!</p>
                <p className="text-[#2CB67D] text-xl">Accuracy: {roundAccuracies[roundAccuracies.length - 1]}%</p>
                <p className="text-slate-400 mt-2">Next round starting...</p>
              </div>
            </div>
          )}
          
          {/* Game finished overlay */}
          {roundAccuracies.length >= totalRounds && (
            <div className="absolute inset-0 flex items-center justify-center bg-slate-900/80">
              <div className="text-center">
                <p className="text-white text-2xl font-bold mb-2">Test Complete!</p>
                <p className="text-[#2CB67D] text-xl">Final Score: {currentAvg}%</p>
              </div>
            </div>
          )}
        </div>
        
        <p className="text-center text-[#64748B] mt-4 text-sm">
          {isTracking 
            ? `${deviceMode === 'mobile' ? 'Drag your finger' : 'Move your cursor'} to follow the green target`
            : 'Get ready...'
          }
        </p>
        
        {/* Round accuracies */}
        <div className="flex justify-center gap-2 mt-4">
          {[...Array(totalRounds)].map((_, i) => (
            <div 
              key={i}
              className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-mono ${
                i < roundAccuracies.length 
                  ? roundAccuracies[i] >= 70 ? 'bg-[#2CB67D] text-white' : 'bg-[#FF9F1C] text-white'
                  : i === round && isTracking ? 'bg-[#0B1D51] text-white animate-pulse' : 'bg-slate-200 text-slate-400'
              }`}
            >
              {i < roundAccuracies.length ? roundAccuracies[i] : i + 1}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Memory Game - Visual/Spatial Memory Test
const MemoryGame = ({ level, onComplete }) => {
  const [gameState, setGameState] = useState(GAME_STATES.READY);
  const [deviceMode, setDeviceMode] = useState(null);
  const [score, setScore] = useState(0);
  const [round, setRound] = useState(0);
  const [sequence, setSequence] = useState([]);
  const [userSequence, setUserSequence] = useState([]);
  const [showingSequence, setShowingSequence] = useState(false);
  const [activeCell, setActiveCell] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const totalRounds = 5;
  const gridSize = 9;
  const startTime = useRef(Date.now());
  const correct = useRef(0);

  const baseLength = 3 + Math.floor(level / 2);

  const generateSequence = useCallback(() => {
    const length = baseLength + round;
    const seq = [];
    for (let i = 0; i < length; i++) {
      let cell;
      do {
        cell = Math.floor(Math.random() * gridSize);
      } while (seq.length > 0 && seq[seq.length - 1] === cell);
      seq.push(cell);
    }
    return seq;
  }, [round, baseLength]);

  // Level-based timing: Level 1 slower (700/500ms), Level 5 faster (400/250ms)
  const displayDelay = 700 - (level - 1) * 75; // Time before showing each cell
  const showDuration = 500 - (level - 1) * 62; // How long each cell stays lit

  const showSequence = useCallback(async (seq) => {
    setShowingSequence(true);
    for (let i = 0; i < seq.length; i++) {
      await new Promise(r => setTimeout(r, displayDelay));
      setActiveCell(seq[i]);
      await new Promise(r => setTimeout(r, showDuration));
      setActiveCell(null);
    }
    await new Promise(r => setTimeout(r, 300));
    setShowingSequence(false);
  }, [displayDelay, showDuration]);

  const startRound = useCallback(() => {
    const newSequence = generateSequence();
    setSequence(newSequence);
    setUserSequence([]);
    setFeedback(null);
    showSequence(newSequence);
  }, [generateSequence, showSequence]);

  useEffect(() => {
    if (gameState === GAME_STATES.PLAYING && sequence.length === 0 && !showingSequence) {
      startRound();
    }
  }, [gameState, sequence, showingSequence, startRound]);

  const handleCellClick = (index) => {
    if (showingSequence || gameState !== GAME_STATES.PLAYING) return;
    
    const newUserSequence = [...userSequence, index];
    setUserSequence(newUserSequence);
    setActiveCell(index);
    setTimeout(() => setActiveCell(null), 200);
    
    const currentIndex = newUserSequence.length - 1;
    if (newUserSequence[currentIndex] !== sequence[currentIndex]) {
      setFeedback('incorrect');
      setTimeout(() => {
        if (round + 1 < totalRounds) {
          setRound(r => r + 1);
          setSequence([]);
        } else {
          finishGame();
        }
      }, 1000);
      return;
    }
    
    if (newUserSequence.length === sequence.length) {
      correct.current++;
      setFeedback('correct');
      setScore(s => s + sequence.length * 10);
      setTimeout(() => {
        if (round + 1 < totalRounds) {
          setRound(r => r + 1);
          setSequence([]);
        } else {
          finishGame();
        }
      }, 1000);
    }
  };

  const finishGame = () => {
    const finalScore = Math.round((correct.current / totalRounds) * 100);
    setGameState(GAME_STATES.FINISHED);
    onComplete({
      score: finalScore,
      maxScore: 100,
      accuracy: finalScore,
      completionTime: Math.floor((Date.now() - startTime.current) / 1000)
    });
  };

  if (gameState === GAME_STATES.READY) {
    return (
      <DeviceModeSelector 
        onSelect={(mode) => { 
          setDeviceMode(mode); 
          setGameState(GAME_STATES.DEVICE_SELECT);
        }} 
        testName="Memory Sequence Test"
      />
    );
  }

  if (gameState === GAME_STATES.DEVICE_SELECT) {
    return (
      <Card className="border-slate-200">
        <CardContent className="pt-6 sm:pt-8">
          <div className="text-center mb-4 sm:mb-6">
            <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 bg-[#2CB67D]/10 rounded-full flex items-center justify-center">
              <Database className="w-8 h-8 sm:w-10 sm:h-10 text-[#2CB67D]" />
            </div>
            <h2 className="font-heading text-xl sm:text-2xl font-bold text-[#0B1D51] mb-2">
              Memory Sequence
            </h2>
            <p className="text-sm text-[#64748B]">Level {level} - Visual Memory Assessment</p>
            <p className="text-xs text-[#FF9F1C] font-medium mt-2">
              Develops skills to improve performance on TRP1 Trainability Tests
            </p>
          </div>
          
          <div className="bg-[#F8FAFC] rounded-sm p-3 sm:p-4 mb-4 sm:mb-6">
            <h3 className="font-semibold text-[#0B1D51] mb-3 text-sm sm:text-base">How to Play:</h3>
            <ol className="space-y-2 text-xs sm:text-sm text-[#64748B]">
              <li className="flex gap-2">
                <span className="font-mono text-[#0B1D51]">1.</span>
                Watch the cells light up in sequence
              </li>
              <li className="flex gap-2">
                <span className="font-mono text-[#0B1D51]">2.</span>
                Memorise the order of the sequence
              </li>
              <li className="flex gap-2">
                <span className="font-mono text-[#0B1D51]">3.</span>
                {deviceMode === 'mobile' ? 'Tap' : 'Click'} the cells in the same order
              </li>
              <li className="flex gap-2">
                <span className="font-mono text-[#0B1D51]">4.</span>
                Complete 5 rounds with increasing difficulty
              </li>
            </ol>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 mb-4 sm:mb-6">
            <p className="text-xs sm:text-sm text-amber-800">
              <strong>RIS-3751-TOM:</strong> This exercise develops skills assessed by TRP1 - measuring ability to remember and recall sequences of visual information essential for operational procedures.
            </p>
          </div>
          
          <Button 
            onClick={() => {
              setGameState(GAME_STATES.PLAYING);
              startTime.current = Date.now();
            }}
            size="lg"
            className="w-full bg-[#0B1D51] hover:bg-[#0B1D51]/90"
            data-testid="start-game-btn"
          >
            <Play className="w-5 h-5 mr-2" />
            Start Test
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="min-h-[500px] flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex justify-between items-center mb-4 text-sm">
          <span className="font-mono text-[#64748B]">Round {round + 1}/{totalRounds}</span>
          <span className="font-mono text-[#0B1D51]">Sequence: {sequence.length} cells</span>
        </div>
        
        <div className="text-center mb-4 h-8">
          {showingSequence && (
            <p className="text-[#FF9F1C] font-medium">Watch the sequence...</p>
          )}
          {!showingSequence && !feedback && sequence.length > 0 && (
            <p className="text-[#0B1D51] font-medium">Now repeat the sequence ({userSequence.length}/{sequence.length})</p>
          )}
          {feedback === 'correct' && (
            <p className="text-[#2CB67D] font-medium">Correct!</p>
          )}
          {feedback === 'incorrect' && (
            <p className="text-red-500 font-medium">Wrong sequence!</p>
          )}
        </div>
        
        <div className="grid grid-cols-3 gap-3 max-w-[300px] mx-auto">
          {[...Array(gridSize)].map((_, i) => (
            <motion.button
              key={i}
              onClick={() => handleCellClick(i)}
              disabled={showingSequence}
              className={`aspect-square rounded-lg transition-all ${
                activeCell === i 
                  ? 'bg-[#FF9F1C] scale-95' 
                  : 'bg-slate-200 hover:bg-slate-300'
              } ${showingSequence ? 'cursor-not-allowed' : 'cursor-pointer'}`}
              whileTap={{ scale: 0.9 }}
            />
          ))}
        </div>
        
        <div className="mt-6 text-center">
          <p className="text-[#64748B] text-sm">
            Score: {score} | Correct: {correct.current}/{round + (feedback ? 1 : 0)}
          </p>
        </div>
      </div>
    </div>
  );
};

const ReactionTimeGame = ({ level, onComplete }) => {
  const [gameState, setGameState] = useState(GAME_STATES.READY);
  const [deviceMode, setDeviceMode] = useState(null);
  const [score, setScore] = useState(0);
  const [round, setRound] = useState(0);
  const [showTarget, setShowTarget] = useState(false);
  const [reactionTimes, setReactionTimes] = useState([]);
  const [feedback, setFeedback] = useState(null);
  const [waiting, setWaiting] = useState(false);
  const totalRounds = 10 + (level || 1); // More rounds at higher levels
  const targetAppearTime = useRef(null);
  const startTime = useRef(Date.now());
  const correct = useRef(0);
  const wrong = useRef(0);

  // Level-based scoring thresholds (stricter at higher levels)
  const getPoints = (reactionTime) => {
    const levelFactor = level || 1;
    // Level 1: <400ms=15pts, Level 5: <200ms=15pts
    const excellentThreshold = 400 - (levelFactor * 40); // 360, 320, 280, 240, 200
    const goodThreshold = excellentThreshold + 100;
    const okThreshold = goodThreshold + 100;
    
    if (reactionTime < excellentThreshold) return 15;
    if (reactionTime < goodThreshold) return 12;
    if (reactionTime < okThreshold) return 10;
    return 5;
  };

  const startRound = useCallback(() => {
    setWaiting(true);
    setShowTarget(false);
    
    // Shorter delays at higher levels (less predictable)
    const levelFactor = level || 1;
    const minDelay = Math.max(800, 1500 - (levelFactor * 140)); // 1500 → 800
    const maxDelay = Math.max(2000, 4000 - (levelFactor * 400)); // 4000 → 2000
    const delay = minDelay + Math.random() * (maxDelay - minDelay);
    
    setTimeout(() => {
      setShowTarget(true);
      targetAppearTime.current = Date.now();
      setWaiting(false);
    }, delay);
  }, [level]);

  useEffect(() => {
    if (gameState === GAME_STATES.PLAYING && round < totalRounds && !waiting && !showTarget) {
      startRound();
    }
  }, [gameState, round, waiting, showTarget, startRound, totalRounds]);

  const handleClick = () => {
    if (gameState !== GAME_STATES.PLAYING) return;
    
    if (waiting) {
      wrong.current++;
      setFeedback({ type: 'wrong', message: 'Too early!' });
      setScore(s => Math.max(0, s - 5));
      setTimeout(() => {
        setFeedback(null);
        setRound(r => r + 1);
      }, 1000);
      return;
    }
    
    if (showTarget) {
      const reactionTime = Date.now() - targetAppearTime.current;
      setReactionTimes(prev => [...prev, reactionTime]);
      correct.current++;
      
      const points = getPoints(reactionTime);
      
      setScore(s => s + points);
      setFeedback({ type: 'correct', message: `${reactionTime}ms` });
      setShowTarget(false);
      
      setTimeout(() => {
        setFeedback(null);
        if (round + 1 >= totalRounds) {
          finishGame();
        } else {
          setRound(r => r + 1);
        }
      }, 1000);
    }
  };

  const finishGame = () => {
    setGameState(GAME_STATES.FINISHED);
    const timeSpent = Math.round((Date.now() - startTime.current) / 1000);
    const accuracy = correct.current + wrong.current > 0 
      ? (correct.current / (correct.current + wrong.current)) * 100 
      : 0;
    onComplete({ score, maxScore: totalRounds * 15, accuracy, timeSpent });
  };

  const startGame = () => {
    setGameState(GAME_STATES.PLAYING);
    setScore(0);
    setRound(0);
    setReactionTimes([]);
    correct.current = 0;
    wrong.current = 0;
    startTime.current = Date.now();
  };

  if (gameState === GAME_STATES.READY) {
    return <DeviceModeSelector onSelect={(mode) => { setDeviceMode(mode); setGameState(GAME_STATES.DEVICE_SELECT); }} />;
  }

  if (gameState === GAME_STATES.DEVICE_SELECT) {
    return (
      <Card className="border-slate-200">
        <CardContent className="pt-6 sm:pt-8">
          <div className="text-center mb-4 sm:mb-6">
            <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 bg-[#EF4444]/10 rounded-full flex items-center justify-center">
              <Zap className="w-8 h-8 sm:w-10 sm:h-10 text-[#EF4444]" />
            </div>
            <h2 className="font-heading text-xl sm:text-2xl font-bold text-[#0B1D51] mb-2">
              Response Speed
            </h2>
            <p className="text-sm text-[#64748B]">Level {level} - Reaction Time</p>
            <p className="text-xs text-[#FF9F1C] font-medium mt-2">
              Develops skills to improve performance on WAFV/ATAVT Reaction Time Tests
            </p>
          </div>
          
          <div className="bg-[#F8FAFC] rounded-sm p-3 sm:p-4 mb-4 sm:mb-6">
            <h3 className="font-semibold text-[#0B1D51] mb-3 text-sm sm:text-base">How to Play:</h3>
            <ol className="space-y-2 text-xs sm:text-sm text-[#64748B]">
              <li className="flex gap-2">
                <span className="font-mono text-[#0B1D51]">1.</span>
                Wait for the screen to turn RED
              </li>
              <li className="flex gap-2">
                <span className="font-mono text-[#0B1D51]">2.</span>
                Tap/click as fast as possible when RED appears
              </li>
              <li className="flex gap-2">
                <span className="font-mono text-[#0B1D51]">3.</span>
                Do NOT tap while waiting - loses points
              </li>
            </ol>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 mb-4 sm:mb-6">
            <p className="text-xs sm:text-sm text-amber-800">
              <strong>RIS-3751-TOM:</strong> This exercise develops skills assessed by WAFV and ATAVT - measuring quick and accurate response to visual stimuli, critical for emergency response.
            </p>
          </div>
          
          <Button 
            onClick={startGame}
            size="lg"
            className="w-full bg-[#0B1D51] hover:bg-[#0B1D51]/90"
            data-testid="start-game-btn"
          >
            <Play className="w-5 h-5 mr-2" />
            Start Test
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex items-center justify-between">
        <ScoreDisplay score={score} />
        <Badge variant="outline">Round {round + 1}/{totalRounds}</Badge>
      </div>
      
      <div 
        className={`relative min-h-[350px] sm:min-h-[400px] rounded-sm cursor-pointer flex items-center justify-center transition-colors ${
          waiting ? 'bg-slate-200' : showTarget ? 'bg-[#EF4444]' : 'bg-slate-100'
        }`}
        onClick={handleClick}
      >
        {waiting && <p className="text-xl font-heading font-bold text-[#64748B]">Wait...</p>}
        {showTarget && (
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="text-center">
            <AlertTriangle className="w-20 h-20 sm:w-24 sm:h-24 text-white mx-auto mb-4" />
            <p className="text-2xl sm:text-3xl font-heading font-bold text-white">TAP NOW!</p>
          </motion.div>
        )}
        {!waiting && !showTarget && <p className="text-xl font-heading font-bold text-[#64748B]">Get ready...</p>}
        
        {feedback && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`absolute bottom-4 left-1/2 -translate-x-1/2 px-6 py-3 rounded-sm font-mono font-medium ${
              feedback.type === 'correct' ? 'bg-[#2CB67D] text-white' : 'bg-[#EF4444] text-white'
            }`}
          >
            {feedback.message}
          </motion.div>
        )}
      </div>
    </div>
  );
};

// Reasoning Game - Pattern Recognition and Logical Reasoning
const ReasoningGame = ({ level, onComplete }) => {
  const [gameState, setGameState] = useState(GAME_STATES.READY);
  const [deviceMode, setDeviceMode] = useState(null);
  const [score, setScore] = useState(0);
  const [round, setRound] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const totalRounds = 8;
  const startTime = useRef(Date.now());
  const correct = useRef(0);

  // Questions organized by difficulty level to prevent repetition
  const questionsByLevel = useMemo(() => ({
    1: [ // Level 1 - Basic patterns
      { type: 'number_sequence', sequence: [2, 4, 6, 8, '?'], options: [9, 10, 11, 12], correct: 10, explanation: 'Add 2 each time' },
      { type: 'number_sequence', sequence: [5, 10, 15, 20, '?'], options: [22, 25, 30, 24], correct: 25, explanation: 'Add 5 each time' },
      { type: 'number_sequence', sequence: [3, 6, 9, 12, '?'], options: [14, 15, 16, 18], correct: 15, explanation: 'Add 3 each time (multiples of 3)' },
      { type: 'number_sequence', sequence: [10, 20, 30, 40, '?'], options: [45, 50, 55, 60], correct: 50, explanation: 'Add 10 each time' },
      { type: 'number_sequence', sequence: [1, 2, 3, 4, '?'], options: [5, 6, 7, 8], correct: 5, explanation: 'Add 1 each time' },
      { type: 'number_sequence', sequence: [100, 90, 80, 70, '?'], options: [50, 55, 60, 65], correct: 60, explanation: 'Subtract 10 each time' },
      { type: 'analogy', question: 'HOT is to COLD as UP is to:', options: ['High', 'Down', 'Sky', 'Top'], correct: 'Down', explanation: 'Opposites' },
      { type: 'analogy', question: 'BIRD is to FLY as FISH is to:', options: ['Water', 'Swim', 'Fin', 'Ocean'], correct: 'Swim', explanation: 'How they move' },
      { type: 'analogy', question: 'DAY is to NIGHT as SUMMER is to:', options: ['Cold', 'Winter', 'Snow', 'Dark'], correct: 'Winter', explanation: 'Opposites in time/season' },
      { type: 'analogy', question: 'BOOK is to READ as FOOD is to:', options: ['Cook', 'Eat', 'Kitchen', 'Hungry'], correct: 'Eat', explanation: 'What you do with it' },
      { type: 'logic', question: 'All cats have tails. Tom is a cat. Therefore:', options: ['Tom has whiskers', 'Tom has a tail', 'Tom can fly', 'Tom is big'], correct: 'Tom has a tail', explanation: 'Basic logical deduction' },
      { type: 'logic', question: 'If it is raining, the ground is wet. It is raining. Therefore:', options: ['It will stop', 'The ground is wet', 'It is cold', 'We need umbrellas'], correct: 'The ground is wet', explanation: 'If-then logic' },
    ],
    2: [ // Level 2 - Intermediate patterns
      { type: 'number_sequence', sequence: [2, 4, 8, 16, '?'], options: [24, 32, 20, 28], correct: 32, explanation: 'Each number doubles (×2)' },
      { type: 'number_sequence', sequence: [3, 6, 12, 24, '?'], options: [36, 48, 42, 30], correct: 48, explanation: 'Each number doubles (×2)' },
      { type: 'number_sequence', sequence: [1, 4, 9, 16, '?'], options: [20, 25, 24, 21], correct: 25, explanation: 'Square numbers: 1², 2², 3², 4², 5²' },
      { type: 'number_sequence', sequence: [81, 64, 49, 36, '?'], options: [16, 25, 30, 20], correct: 25, explanation: 'Square numbers descending: 9², 8², 7², 6², 5²' },
      { type: 'number_sequence', sequence: [1, 3, 6, 10, '?'], options: [13, 14, 15, 16], correct: 15, explanation: 'Triangular numbers: +2, +3, +4, +5' },
      { type: 'number_sequence', sequence: [2, 6, 12, 20, '?'], options: [28, 30, 32, 36], correct: 30, explanation: 'Difference increases by 2: +4, +6, +8, +10' },
      { type: 'analogy', question: 'TRAIN is to TRACK as CAR is to:', options: ['Wheel', 'Road', 'Engine', 'Fuel'], correct: 'Road', explanation: 'What they travel on' },
      { type: 'analogy', question: 'SIGNAL is to STOP as GREEN is to:', options: ['Slow', 'Go', 'Wait', 'Caution'], correct: 'Go', explanation: 'Red signal means stop, green means go' },
      { type: 'analogy', question: 'DRIVER is to CONTROL as PASSENGER is to:', options: ['Travel', 'Pay', 'Walk', 'Drive'], correct: 'Travel', explanation: 'Driver controls, passenger travels' },
      { type: 'analogy', question: 'ENGINE is to POWER as BRAKE is to:', options: ['Speed', 'Stop', 'Wheel', 'Metal'], correct: 'Stop', explanation: 'Function of each component' },
      { type: 'logic', question: 'If all signals are red, and red means stop, then:', options: ['Some trains can go', 'All trains must stop', 'Trains should slow down', 'Signals are broken'], correct: 'All trains must stop', explanation: 'Logical deduction from rules' },
      { type: 'logic', question: 'Train A departs at 10:00 and takes 30 min. Train B departs at 10:15 and takes 20 min. Which arrives first?', options: ['Train A', 'Train B', 'Same time', 'Cannot tell'], correct: 'Train A', explanation: 'A: 10:30, B: 10:35' },
    ],
    3: [ // Level 3 - Advanced patterns
      { type: 'number_sequence', sequence: [1, 1, 2, 3, 5, '?'], options: [6, 7, 8, 9], correct: 8, explanation: 'Fibonacci: sum of previous two' },
      { type: 'number_sequence', sequence: [2, 3, 5, 7, 11, '?'], options: [12, 13, 14, 15], correct: 13, explanation: 'Prime numbers' },
      { type: 'number_sequence', sequence: [1, 8, 27, 64, '?'], options: [100, 125, 216, 81], correct: 125, explanation: 'Cube numbers: 1³, 2³, 3³, 4³, 5³' },
      { type: 'number_sequence', sequence: [100, 95, 85, 70, '?'], options: [60, 55, 50, 45], correct: 50, explanation: 'Subtract 5, 10, 15, 20...' },
      { type: 'number_sequence', sequence: [3, 5, 9, 17, '?'], options: [25, 29, 33, 37], correct: 33, explanation: 'Double and subtract 1: ×2-1' },
      { type: 'number_sequence', sequence: [7, 14, 21, 28, '?'], options: [32, 35, 36, 42], correct: 35, explanation: 'Multiples of 7' },
      { type: 'analogy', question: 'SPEEDOMETER is to SPEED as FUEL GAUGE is to:', options: ['Petrol', 'Distance', 'Fuel level', 'Engine'], correct: 'Fuel level', explanation: 'What each instrument measures' },
      { type: 'analogy', question: 'DEPARTURE is to ARRIVAL as START is to:', options: ['Begin', 'Middle', 'End', 'Stop'], correct: 'End', explanation: 'Beginning and end of journey' },
      { type: 'analogy', question: 'PUNCTUAL is to LATE as EARLY is to:', options: ['Morning', 'Delayed', 'Soon', 'Quick'], correct: 'Delayed', explanation: 'Opposites in timing' },
      { type: 'analogy', question: 'CONDUCTOR is to PASSENGERS as CAPTAIN is to:', options: ['Ship', 'Ocean', 'Crew', 'Uniform'], correct: 'Crew', explanation: 'Person in charge of people' },
      { type: 'logic', question: 'Platform 3 is for northbound trains. The 10:15 goes north. Where does the 10:15 depart?', options: ['Platform 1', 'Platform 2', 'Platform 3', 'Platform 4'], correct: 'Platform 3', explanation: 'Northbound = Platform 3' },
      { type: 'logic', question: 'Express trains skip stations B and D. A local train stops at A, B, C, D, E. An express stops at:', options: ['A, B, C', 'A, C, E', 'B, D only', 'All stations'], correct: 'A, C, E', explanation: 'Skips B and D' },
    ],
    4: [ // Level 4 - Complex patterns
      { type: 'number_sequence', sequence: [2, 6, 14, 30, '?'], options: [46, 54, 62, 70], correct: 62, explanation: 'Pattern: ×2+2, ×2+2...' },
      { type: 'number_sequence', sequence: [1, 2, 6, 24, '?'], options: [48, 60, 100, 120], correct: 120, explanation: 'Factorials: 1!, 2!, 3!, 4!, 5!' },
      { type: 'number_sequence', sequence: [3, 4, 7, 11, 18, '?'], options: [25, 27, 29, 31], correct: 29, explanation: 'Add previous two numbers' },
      { type: 'number_sequence', sequence: [4, 9, 16, 25, '?'], options: [30, 34, 36, 49], correct: 36, explanation: 'Squares: 2², 3², 4², 5², 6²' },
      { type: 'number_sequence', sequence: [1, 4, 10, 22, '?'], options: [42, 44, 46, 48], correct: 46, explanation: 'Double previous and add: ×2+2, ×2+2' },
      { type: 'number_sequence', sequence: [8, 27, 64, 125, '?'], options: [196, 216, 256, 289], correct: 216, explanation: 'Cubes: 2³, 3³, 4³, 5³, 6³' },
      { type: 'analogy', question: 'MILLISECOND is to SECOND as SECOND is to:', options: ['Minute', 'Hour', 'Day', 'Millisecond'], correct: 'Minute', explanation: 'Units of time in order' },
      { type: 'analogy', question: 'ACCELERATION is to SPEED as SPEED is to:', options: ['Fast', 'Distance', 'Time', 'Slow'], correct: 'Distance', explanation: 'Rate of change relationship' },
      { type: 'analogy', question: 'TIMETABLE is to SCHEDULE as MAP is to:', options: ['Paper', 'Route', 'Navigate', 'Geography'], correct: 'Route', explanation: 'What each helps you follow' },
      { type: 'logic', question: 'If Train X is faster than Y, and Y is faster than Z, which is slowest?', options: ['Train X', 'Train Y', 'Train Z', 'Cannot determine'], correct: 'Train Z', explanation: 'Transitive comparison' },
      { type: 'logic', question: 'A train runs every 12 minutes. Another every 18 minutes. Both leave at 9:00. When do they next leave together?', options: ['9:24', '9:30', '9:36', '10:00'], correct: '9:36', explanation: 'LCM of 12 and 18 is 36' },
      { type: 'logic', question: 'Journey takes 2h at 60mph. If speed increases to 80mph, journey takes:', options: ['1h 20m', '1h 30m', '1h 40m', '1h 50m'], correct: '1h 30m', explanation: 'Distance=120mi, 120÷80=1.5h' },
    ],
    5: [ // Level 5 - Expert patterns
      { type: 'number_sequence', sequence: [2, 3, 5, 8, 13, '?'], options: [18, 20, 21, 22], correct: 21, explanation: 'Lucas numbers (like Fibonacci but starts 2,3)' },
      { type: 'number_sequence', sequence: [1, 4, 13, 40, '?'], options: [81, 100, 121, 144], correct: 121, explanation: 'Pattern: ×3+1' },
      { type: 'number_sequence', sequence: [6, 11, 21, 36, 56, '?'], options: [71, 76, 81, 86], correct: 81, explanation: 'Differences: 5,10,15,20,25' },
      { type: 'number_sequence', sequence: [99, 92, 86, 81, '?'], options: [75, 76, 77, 78], correct: 77, explanation: 'Subtract 7, 6, 5, 4...' },
      { type: 'number_sequence', sequence: [2, 5, 11, 23, '?'], options: [35, 41, 47, 53], correct: 47, explanation: 'Pattern: ×2+1' },
      { type: 'number_sequence', sequence: [1, 3, 7, 15, 31, '?'], options: [47, 55, 63, 71], correct: 63, explanation: 'Pattern: ×2+1 (2^n - 1)' },
      { type: 'analogy', question: 'THROTTLE is to ACCELERATION as REGULATOR is to:', options: ['Speed', 'Control', 'Pressure', 'Steam'], correct: 'Pressure', explanation: 'What each controls in locomotives' },
      { type: 'analogy', question: 'DERAILMENT is to TRACK as COLLISION is to:', options: ['Object', 'Speed', 'Damage', 'Brake'], correct: 'Object', explanation: 'Cause of each type of incident' },
      { type: 'logic', question: 'A 300m train enters a 500m tunnel at 30m/s. How long until completely through?', options: ['16.7s', '26.7s', '10s', '36.7s'], correct: '26.7s', explanation: 'Total distance: 800m, 800÷30≈26.7s' },
      { type: 'logic', question: 'Platform capacity: 200. Train A brings 80, takes 50. Train B brings 100, takes 90. After both, how many on platform?', options: ['30', '40', '50', '60'], correct: '40', explanation: '200+80-50+100-90=240, but max 200, so +80-50+100-90 net = +40' },
      { type: 'logic', question: 'Signal sequence: G-Y-R takes 90s total. Green is twice yellow, yellow is twice red. How long is green?', options: ['45s', '50s', '51.4s', '60s'], correct: '51.4s', explanation: 'R=x, Y=2x, G=4x; 7x=90; x≈12.9; G≈51.4s' },
      { type: 'logic', question: 'Two trains approach head-on, 300km apart, at 100km/h and 50km/h. When do they meet?', options: ['1h', '2h', '3h', '2.5h'], correct: '2h', explanation: 'Combined speed 150km/h, 300÷150=2h' },
    ]
  }), []);

  const questions = useMemo(() => {
    // Get questions for current level (fallback to level 1 if invalid)
    const levelQuestions = questionsByLevel[level] || questionsByLevel[1];
    
    // Shuffle questions AND shuffle options within each question
    return [...levelQuestions]
      .sort(() => Math.random() - 0.5)
      .slice(0, totalRounds)
      .map(q => ({
        ...q,
        options: [...q.options].sort(() => Math.random() - 0.5)
      }));
  }, [level, questionsByLevel]);

  useEffect(() => {
    if (gameState === GAME_STATES.PLAYING && !currentQuestion) {
      setCurrentQuestion(questions[round]);
    }
  }, [gameState, currentQuestion, questions, round]);

  const handleAnswer = (answer) => {
    if (feedback) return;
    setSelectedAnswer(answer);
    
    const isCorrect = answer === currentQuestion.correct;
    if (isCorrect) {
      correct.current++;
      setScore(s => s + 10 + level);
      setFeedback({ type: 'correct', message: 'Correct!' });
    } else {
      setFeedback({ type: 'wrong', message: currentQuestion.explanation });
    }
    
    setTimeout(() => {
      if (round + 1 < totalRounds) {
        setRound(r => r + 1);
        setCurrentQuestion(questions[round + 1]);
        setSelectedAnswer(null);
        setFeedback(null);
      } else {
        finishGame();
      }
    }, 2000);
  };

  const finishGame = () => {
    const finalScore = Math.round((correct.current / totalRounds) * 100);
    setGameState(GAME_STATES.FINISHED);
    onComplete({
      score: finalScore,
      maxScore: 100,
      accuracy: finalScore,
      completionTime: Math.floor((Date.now() - startTime.current) / 1000)
    });
  };

  if (gameState === GAME_STATES.READY) {
    return (
      <DeviceModeSelector 
        onSelect={(mode) => { 
          setDeviceMode(mode); 
          setGameState(GAME_STATES.DEVICE_SELECT);
        }} 
        testName="Reasoning & Logic Test"
      />
    );
  }

  if (gameState === GAME_STATES.DEVICE_SELECT) {
    return (
      <Card className="border-slate-200">
        <CardContent className="pt-6 sm:pt-8">
          <div className="text-center mb-4 sm:mb-6">
            <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 bg-[#2CB67D]/10 rounded-full flex items-center justify-center">
              <Lightbulb className="w-8 h-8 sm:w-10 sm:h-10 text-[#2CB67D]" />
            </div>
            <h2 className="font-heading text-xl sm:text-2xl font-bold text-[#0B1D51] mb-2">
              Problem Solving
            </h2>
            <p className="text-sm text-[#64748B]">Level {level} - Cognitive Assessment</p>
            <p className="text-xs text-[#FF9F1C] font-medium mt-2">
              Develops skills to improve performance on TRP2 Trainability Problem-Solving Test
            </p>
          </div>
          
          <div className="bg-[#F8FAFC] rounded-sm p-3 sm:p-4 mb-4 sm:mb-6">
            <h3 className="font-semibold text-[#0B1D51] mb-3 text-sm sm:text-base">How to Play:</h3>
            <ol className="space-y-2 text-xs sm:text-sm text-[#64748B]">
              <li className="flex gap-2">
                <span className="font-mono text-[#0B1D51]">1.</span>
                Read each question carefully
              </li>
              <li className="flex gap-2">
                <span className="font-mono text-[#0B1D51]">2.</span>
                Identify patterns in number sequences
              </li>
              <li className="flex gap-2">
                <span className="font-mono text-[#0B1D51]">3.</span>
                Complete analogies and logical problems
              </li>
              <li className="flex gap-2">
                <span className="font-mono text-[#0B1D51]">4.</span>
                Select the correct answer from the options
              </li>
            </ol>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 mb-4 sm:mb-6">
            <p className="text-xs sm:text-sm text-amber-800">
              <strong>RIS-3751-TOM:</strong> This exercise develops skills assessed by TRP2 - measuring ability to identify patterns, solve problems, and apply logical thinking under time pressure.
            </p>
          </div>
          
          <Button 
            onClick={() => {
              setGameState(GAME_STATES.PLAYING);
              startTime.current = Date.now();
            }}
            size="lg"
            className="w-full bg-[#0B1D51] hover:bg-[#0B1D51]/90"
            data-testid="start-game-btn"
          >
            <Play className="w-5 h-5 mr-2" />
            Start Test
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!currentQuestion) return null;

  return (
    <div className="min-h-[500px] flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <div className="flex justify-between items-center mb-6 text-sm">
          <span className="font-mono text-[#64748B]">Question {round + 1}/{totalRounds}</span>
          <span className="font-mono text-[#0B1D51]">Score: {score}</span>
        </div>
        
        <Card className="border-slate-200 mb-6">
          <CardContent className="pt-6">
            {currentQuestion.type === 'number_sequence' && (
              <div className="text-center">
                <p className="text-[#64748B] mb-3 text-sm">What comes next in the sequence?</p>
                <div className="flex items-center justify-center gap-3 flex-wrap">
                  {currentQuestion.sequence.map((num, i) => (
                    <span 
                      key={i} 
                      className={`w-12 h-12 flex items-center justify-center rounded-lg font-mono text-lg font-bold ${
                        num === '?' ? 'bg-[#FF9F1C] text-white' : 'bg-slate-100 text-[#0B1D51]'
                      }`}
                    >
                      {num}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {(currentQuestion.type === 'analogy' || currentQuestion.type === 'logic') && (
              <div className="text-center">
                <p className="text-[#0B1D51] font-medium text-lg">{currentQuestion.question}</p>
              </div>
            )}
          </CardContent>
        </Card>
        
        <div className="grid grid-cols-2 gap-3">
          {currentQuestion.options.map((option, i) => (
            <motion.button
              key={i}
              onClick={() => handleAnswer(option)}
              disabled={!!feedback}
              className={`p-4 rounded-lg border-2 font-medium transition-all ${
                selectedAnswer === option
                  ? feedback?.type === 'correct'
                    ? 'bg-[#2CB67D] border-[#2CB67D] text-white'
                    : 'bg-red-500 border-red-500 text-white'
                  : option === currentQuestion.correct && feedback
                    ? 'bg-[#2CB67D] border-[#2CB67D] text-white'
                    : 'bg-white border-slate-200 hover:border-[#0B1D51] text-[#0B1D51]'
              }`}
              whileHover={{ scale: feedback ? 1 : 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {option}
            </motion.button>
          ))}
        </div>
        
        {feedback && (
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mt-4 p-3 rounded-lg text-center ${
              feedback.type === 'correct' ? 'bg-[#2CB67D]/10 text-[#2CB67D]' : 'bg-red-50 text-red-600'
            }`}
          >
            {feedback.message}
          </motion.div>
        )}
        
        <div className="mt-6 text-center">
          <p className="text-[#64748B] text-sm">Correct: {correct.current}/{round + (feedback ? 1 : 0)}</p>
        </div>
      </div>
    </div>
  );
};

// =====================
// MAIN GAME PAGE COMPONENT
// =====================
export const GamePage = () => {
  const { slug, level } = useParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [test, setTest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState(null);
  const [accessStatus, setAccessStatus] = useState(null);
  const [usingCredit, setUsingCredit] = useState(false);
  const [gameSessionId, setGameSessionId] = useState(null);
  const [gameStarted, setGameStarted] = useState(false);
  const [guestMode, setGuestMode] = useState(false);
  const levelNum = parseInt(level) || 1;
  const isNorthernAssessment = slug === 'northern-online-screening';

  useEffect(() => {
    const loadData = async () => {
      try {
        const testRes = await apiClient.getTest(slug);
        setTest(testRes.data);
        
        // Check access if user is logged in
        if (user) {
          try {
            const accessRes = await apiClient.checkCreditAccess(slug);
            setAccessStatus(accessRes.data);
            // If there's an active session, use it
            if (accessRes.data.has_active_session && accessRes.data.session_id) {
              setGameSessionId(accessRes.data.session_id);
              setGameStarted(true);
            }
          } catch (e) {
            setAccessStatus({ is_member: false, free_credits: 0, can_access: false });
          }
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [slug, user]);

  // Check if guest has already used their free trial
  // Preview environment allows unlimited guest trials for testing
  const isPreviewEnv = window.location.hostname.includes('preview.emergentagent.com');
  const hasUsedGuestTrial = () => {
    if (isPreviewEnv) return false; // Unlimited trials in preview
    const guestTrials = JSON.parse(localStorage.getItem('guestTrials') || '[]');
    return guestTrials.length > 0;
  };

  // Mark guest trial as used (skip in preview environment)
  const markGuestTrialUsed = (testSlug) => {
    if (isPreviewEnv) return; // Don't track in preview
    const guestTrials = JSON.parse(localStorage.getItem('guestTrials') || '[]');
    if (!guestTrials.includes(testSlug)) {
      guestTrials.push(testSlug);
      localStorage.setItem('guestTrials', JSON.stringify(guestTrials));
    }
  };

  // Start guest trial
  const handleStartGuestTrial = () => {
    markGuestTrialUsed(slug);
    setGuestMode(true);
    setGameStarted(true);
  };

  const handleUseFreeCredit = async () => {
    setUsingCredit(true);
    try {
      const response = await apiClient.useFreeCredit(slug);
      await refreshUser();
      // Store the session ID for score saving
      setGameSessionId(response.data.session_id);
      setGameStarted(true);
      // Refresh access status
      const accessRes = await apiClient.checkCreditAccess(slug);
      setAccessStatus(accessRes.data);
      toast.success('Credit used! Starting test...');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to use credit');
    } finally {
      setUsingCredit(false);
    }
  };

  const handleGameComplete = async (gameResults) => {
    // Normalize different property names from game components
    const normalizedResults = {
      ...gameResults,
      timeSpent: gameResults.timeSpent || gameResults.completionTime || 0,
      isGuestTrial: guestMode, // Flag for results display
    };
    setResults(normalizedResults);
    
    if (user) {
      try {
        await apiClient.saveScore({
          test_slug: slug,
          level: levelNum,
          score: normalizedResults.score,
          max_score: normalizedResults.maxScore,
          accuracy: normalizedResults.accuracy,
          completion_time_seconds: normalizedResults.timeSpent,
          session_id: gameSessionId, // Include session ID for credit-based access
        });
        toast.success('Score saved!');
      } catch (error) {
        console.error('Failed to save score:', error, 'payload:', {
          test_slug: slug, level: levelNum,
          score: normalizedResults.score, max_score: normalizedResults.maxScore,
          accuracy: normalizedResults.accuracy, completion_time_seconds: normalizedResults.timeSpent,
        });
        toast.error(error.response?.data?.detail
          ? `Couldn't save score: ${typeof error.response.data.detail === 'string' ? error.response.data.detail : 'please try again'}`
          : "Couldn't save your score. Please try again.");
      }
    } else if (guestMode) {
      // Guest completed - show toast encouraging signup
      toast.info('Create an account to save your scores and track progress!');
    }
  };

  const handleReplay = () => {
    setResults(null);
    // Reset game session for credit users (they need to spend another credit)
    if (!accessStatus?.is_member && gameSessionId) {
      setGameStarted(false);
      setGameSessionId(null);
    }
    // Reset guest mode - they need to sign up for more attempts
    if (guestMode) {
      setGuestMode(false);
      setGameStarted(false);
    }
  };
  const handleNext = () => { 
    setResults(null); 
    // Reset game session for credit users
    if (!accessStatus?.is_member && gameSessionId) {
      setGameStarted(false);
      setGameSessionId(null);
    }
    navigate(`/play/${slug}/${levelNum + 1}`); 
  };
  const handleBack = () => navigate(`/tests/${slug}`);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0B1D51]" />
      </div>
    );
  }

  // Test under maintenance: block all users (admins may still preview with a banner)
  if (test?.is_maintenance && !user?.is_admin) {
    return (
      <GameWrapper test={test} level={levelNum} onBack={handleBack} slug={slug}>
        <MaintenanceNotice
          testName={test.name}
          message={test.maintenance_message}
          startedAt={test.maintenance_started_at}
        />
      </GameWrapper>
    );
  }

  // Check access — unified entitlement (single source of truth), see lib/entitlements.js
  const { hasUnlimitedAccess, hasCredits } = getEntitlements(user, test?.slug, accessStatus);
  const isActiveMember = hasUnlimitedAccess;

  // Guest can access if they haven't used their trial yet OR are in guest mode
  const guestCanTry = !isNorthernAssessment && !user && !hasUsedGuestTrial();
  
  // Can access if: member/subscriber, has credits, has active game session, or guest mode active
  const canAccess = hasUnlimitedAccess || gameStarted || accessStatus?.has_active_session || guestMode;

  // Show access required screen if not member and hasn't started game (no active session)
  if (!canAccess && !hasCredits && !guestCanTry) {
    return (
      <GameWrapper test={test} level={levelNum} onBack={handleBack} slug={slug}>
        <Card className="border-slate-200 max-w-md mx-auto">
          <CardContent className="pt-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-[#FF9F1C]/10 rounded-full flex items-center justify-center">
              <Crown className="w-8 h-8 text-[#FF9F1C]" />
            </div>
            <h2 className="font-heading text-2xl font-bold text-[#0B1D51] mb-2">
              {!user ? 'Free Trial Used' : 'Access Required'}
            </h2>
            
            {!user ? (
              <>
                <p className="text-[#64748B] mb-6">
                  You've used your free trial. Create an account to get 5 more free attempts!
                </p>
                <div className="space-y-3">
                  <Link to="/?auth=register">
                    <Button className="w-full bg-[#2CB67D] hover:bg-[#2CB67D]/90">
                      Create Free Account (5 Free Attempts)
                    </Button>
                  </Link>
                  <Link to="/?auth=login">
                    <Button variant="outline" className="w-full">
                      Already have an account? Sign In
                    </Button>
                  </Link>
                </div>
              </>
            ) : (
              <>
                <p className="text-[#64748B] mb-6">
                  You have no credits remaining. Subscribe for unlimited access to all tests.
                </p>
                <div className="space-y-3">
                  <Link to="/pricing">
                    <Button className="w-full bg-[#FF9F1C] hover:bg-[#FF9F1C]/90">
                      View Pricing Plans
                    </Button>
                  </Link>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </GameWrapper>
    );
  }

  // Show "try free" screen for guests who haven't used their trial
  if (!canAccess && guestCanTry && !gameStarted) {
    return (
      <GameWrapper test={test} level={levelNum} onBack={handleBack} slug={slug}>
        <Card className="border-slate-200 max-w-md mx-auto">
          <CardContent className="pt-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-[#2CB67D]/10 rounded-full flex items-center justify-center">
              <Play className="w-8 h-8 text-[#2CB67D]" />
            </div>
            <h2 className="font-heading text-2xl font-bold text-[#0B1D51] mb-2">
              Try It Free!
            </h2>
            
            <p className="text-[#64748B] mb-4">
              Experience this test with no signup required. Your score won't be saved, but you'll see exactly what to expect.
            </p>
            
            <div className="bg-[#2CB67D]/10 border border-[#2CB67D]/30 rounded-lg p-3 mb-6">
              <p className="text-sm text-[#2CB67D] font-medium">
                1 free trial per device • No account needed
              </p>
            </div>
            
            <div className="space-y-3">
              <Button 
                className="w-full bg-[#2CB67D] hover:bg-[#2CB67D]/90 text-lg py-6"
                onClick={handleStartGuestTrial}
                data-testid="start-guest-trial-btn"
              >
                <Play className="w-5 h-5 mr-2" />
                Start Free Trial
              </Button>
              
              <div className="relative py-2">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-[#64748B]">or</span>
                </div>
              </div>
              
              <Link to="/?auth=register">
                <Button variant="outline" className="w-full">
                  Create Account for 5 Free Attempts
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </GameWrapper>
    );
  }

  // Show "use credit to start" screen for users with credits but no active session
  if (!canAccess && hasCredits && !gameStarted) {
    return (
      <GameWrapper test={test} level={levelNum} onBack={handleBack} slug={slug}>
        <Card className="border-slate-200 max-w-md mx-auto">
          <CardContent className="pt-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-[#2CB67D]/10 rounded-full flex items-center justify-center">
              <Play className="w-8 h-8 text-[#2CB67D]" />
            </div>
            <h2 className="font-heading text-2xl font-bold text-[#0B1D51] mb-2">
              Ready to Start?
            </h2>
            
            <p className="text-[#64748B] mb-2">
              You have <strong className="text-[#2CB67D]">{user?.free_credits || accessStatus?.free_credits} credit{(user?.free_credits || accessStatus?.free_credits) !== 1 ? 's' : ''}</strong> remaining.
            </p>
            <p className="text-sm text-[#64748B] mb-6">
              Starting this test will use <strong>1 credit</strong>. Each attempt costs 1 credit.
            </p>
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6">
              <p className="text-xs text-amber-800">
                <strong>Note:</strong> Credits are consumed when you start the test, not when you finish. Make sure you're ready to complete the test.
              </p>
            </div>
            
            <div className="space-y-3">
              <Button 
                className="w-full bg-[#2CB67D] hover:bg-[#2CB67D]/90"
                onClick={handleUseFreeCredit}
                disabled={usingCredit}
                data-testid="use-credit-start-btn"
              >
                {usingCredit ? 'Starting...' : `Use 1 Credit & Start Test`}
              </Button>
              <Link to="/pricing">
                <Button variant="outline" className="w-full">
                  Subscribe for Unlimited Access
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </GameWrapper>
    );
  }

  if (results) {
    return (
      <GameWrapper test={test} level={levelNum} onBack={handleBack} slug={slug}>
        <ResultsScreen {...results} onReplay={handleReplay} onNext={handleNext} onBack={handleBack} 
          testSlug={slug} level={levelNum} isMember={hasUnlimitedAccess} isGuestTrial={guestMode} />
      </GameWrapper>
    );
  }

  const renderGame = () => {
    if (slug === 'northern-online-screening') {
      return <NorthernScreeningAssessment gameSessionId={gameSessionId} onBack={handleBack} />;
    }

    const gameProps = { level: levelNum, onComplete: handleGameComplete };
    
    switch (slug) {
      case 'attention-selective': return <SelectiveAttentionGame {...gameProps} />;
      case 'attention-divided': return <DividedAttentionGame {...gameProps} />;
      case 'behavioural': return <BehaviouralGame {...gameProps} />;
      case 'hand-coordination': return <HandCoordinationGame {...gameProps} />;
      case 'memory': return <RulesMemoryGame {...gameProps} />;
      case 'perception': return <PerceptionGame {...gameProps} />;
      case 'reaction-time': return <ReactionTimeGame {...gameProps} />;
      case 'reasoning': return <ReasoningGame {...gameProps} />;
      case 'vigilance': return <VigilanceGame {...gameProps} />;
      case 'rule-based-prioritising': return <DialsGame {...gameProps} />;
      case 'multitasking-workload': return <MultitaskingGame {...gameProps} />;
      case 'sequencing': return <SequencingGame {...gameProps} />;
      case 'rules-acquisition': return <RulesAcquisitionGame {...gameProps} />;
      case 'communication-accuracy': return <CommunicationGame {...gameProps} />;
      case 'detail-checking': return <DetailCheckingGame {...gameProps} />;
      case 'visual-search': return <VisualSearchGame {...gameProps} />;
      default: return <SelectiveAttentionGame {...gameProps} />;
    }
  };

  return (
    <GameWrapper test={test} level={levelNum} onBack={handleBack} slug={slug}>
      {test?.is_maintenance && user?.is_admin && (
        <div className="max-w-3xl mx-auto mb-4">
          <MaintenanceBanner
            message={test.maintenance_message}
            startedAt={test.maintenance_started_at}
            adminView
          />
        </div>
      )}
      {renderGame()}
    </GameWrapper>
  );
};

export default GamePage;
